import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../../api'
import { reloadForNewConfiguration } from '../../core/features/FeaturesProvider'
import { FeatureAdminItem, FeatureStatus } from '../../types/featureTypes'
import { useLanguage } from '../utility/useLanguage'

/** Child of `FEATURES_QUERY_KEY` (FeaturesProvider), so a blanket
 * `['features']` invalidation refreshes the admin list too. */
export const FEATURE_FLAGS_ADMIN_QUERY_KEY = ['features', 'admin'] as const

/**
 * Reshaped-admin view of the runtime feature flags: the full table with
 * stored/effective state, plus the one write, `toggle`.
 *
 * Not to be confused with `useFeatures` / `useFeature` (FeaturesProvider),
 * which is the read-only runtime gate every client renders from.
 *
 * `toggle` is optimistic-locked on the row version the caller holds. On
 * success every client — this tab included — hard-reloads on the new
 * version, so no UI ever runs against a stale configuration. On a 409 (stale
 * version, disabled dependency, missing settings) the axios interceptor has
 * already toasted the backend detail; we refetch so the table is honest again.
 */
export const useFeatureFlags = () => {
  const { t } = useLanguage()
  const queryClient = useQueryClient()

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: FEATURE_FLAGS_ADMIN_QUERY_KEY,
    queryFn: apiClient.features.fetchAdminList,
    staleTime: 0,
    refetchOnWindowFocus: true,
  })

  const toggleMutation = useMutation({
    mutationFn: ({ item, status }: { item: FeatureAdminItem; status: FeatureStatus }) =>
      apiClient.features.toggle(item.id, { status, expected_version: item.version }),
    onSuccess: result => {
      reloadForNewConfiguration(
        t('features.toggled', {
          name: result.feature.name,
          status: result.feature.status,
          defaultValue: `${result.feature.name} switched ${result.feature.status}. Reloading…`,
        }),
      )
    },
    onError: async () => {
      await queryClient.invalidateQueries({ queryKey: FEATURE_FLAGS_ADMIN_QUERY_KEY })
    },
  })

  const toggle = (item: FeatureAdminItem, status: FeatureStatus) => toggleMutation.mutate({ item, status })

  return {
    // Data
    features: data?.features ?? [],
    version: data?.version,
    isLoading,
    error: error?.message || null,

    // Actions
    toggle,
    isToggling: toggleMutation.isPending,
    /** Id of the row whose toggle is in flight, if any. */
    togglingId: toggleMutation.isPending ? toggleMutation.variables?.item.id : undefined,
    refetch,
  }
}
